<script lang="ts" setup>
import { onMounted, onUnmounted, PropType, ref } from "vue";
import { ParametersTemplateModel, TaskParameterModel, TaskParameterTypeEnum } from "@/components";
import {
  PencilSquareIcon,
  TrashIcon,
  ArrowDownTrayIcon,
} from "tir-style-system/icons/outline";
import {
  PopoverPositionEnum,
  TirPmPopover,
  TirPmInput,
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
  TirPmScrollbar,
} from "tir-components";
import { TaskParameter } from "@/components";

/**Параметры компонента */
const props = defineProps({
  /**Тип настройки */
  type: { type: Number as PropType<TaskParameterTypeEnum> },
  /**Список шаблонов */
  templates: { type: Array as PropType<ParametersTemplateModel[]> },
  /**Весь список доступных параметров */
  templateParameters: { type: Array as PropType<TaskParameterModel[]> },
  /**Режим импорта параметров из очередей */
  isImport: { type: Boolean, default: false },
});
/**События */
const emit = defineEmits<{
  /**Удаление шаблона */
  (e: "remove", template: ParametersTemplateModel);
  /**Редактирование шаблона */
  (e: "upsert", template: ParametersTemplateModel);
  /**Выбор шаблона */
  (e: "select", template: ParametersTemplateModel);
  /**Редактировать шаблон */
  (e: "editCompound", template: ParametersTemplateModel);
}>();

/**Элемент обертки */
const $wrapper = ref<HTMLElement>();
/**Максимально допустимая высота списка шаблонов */
const maxHeight = ref();
/**Имя шаблона */
const innerName = ref("");
/**Поповер редактирования шаблона */
const $editPopover = ref<InstanceType<typeof TirPmPopover>[]>();
/**Поповер удаления шаблона */
const $removePopover = ref<InstanceType<typeof TirPmPopover>[]>();
/**Индекс элемента под курсором */
const hoverIndex = ref();

/**После рендера */
onMounted(() => {
  window.addEventListener("resize", windowOnResize);
  windowOnResize();
});
/**Перед удалением */
onUnmounted(() => {
  window.removeEventListener("resize", windowOnResize);
});

/**При изменении размеров окна */
function windowOnResize() {
  const rect = $wrapper.value?.getBoundingClientRect();
  if (rect) {
    maxHeight.value = rect.height / 16 + "rem";
  }
}
/**Проверить, отключена ли кнопка сохранения имени шаблона */
const checkSaveDisable = (template: ParametersTemplateModel) => {
  return !innerName.value || innerName.value == template.Name;
};
/**Изменить имя шаблона */
const editTemplateName = (template: ParametersTemplateModel, index: number) => {
  template.Name = innerName.value;
  $editPopover.value[index]?.close?.();
  emit("upsert", template);
};
/**Начало редактирования состава шаблона */
const startEditTemplate = (template: ParametersTemplateModel, index: number) => {
  $editPopover.value[index]?.close?.();
  emit("editCompound", template);
};
/**Удаление шаблона */
const removeTemplate = (template: ParametersTemplateModel) => {
  emit("remove", template);
};
/**Закрыть поповер удаления шаблона */
const closeRemovePopover = (index: number) => {
  $removePopover.value[index]?.close?.();
};
/**Получение класса элемента списка */
const getItemClass = (index: number) => {
  return { hover: hoverIndex.value == index };
};
/**Установка hover индекса */
const setHoverIndex = (index: number) => {
  hoverIndex.value = index;
};
/**Выбор шаблона */
const selectTemplate = (template: ParametersTemplateModel) => {
  setTimeout(() => emit("select", template));
};
</script>
<template>
  <div class="task-parameters-templates-list" ref="$wrapper">
    <TirPmScrollbar>
      <div class="task-parameters-templates-list_wrapper">
        <TransitionGroup name="task-parameters-templates-list_animation">
          <div
            v-for="(template, index) in templates"
            :key="template.Id"
            class="task-parameters-templates-list_item"
            :class="getItemClass(index)"
            @mousemove="setHoverIndex(index)"
            @mouseleave="setHoverIndex(undefined)"
          >
            <div class="task-parameters-templates-list_item_title">
              {{ template.Name }}
              <template v-if="!isImport">
                <TirPmPopover :position="PopoverPositionEnum.Right" ref="$editPopover">
                  <template #default>
                    <div class="task-parameters-form_button">
                      <PencilSquareIcon />
                    </div>
                  </template>
                  <template #content>
                    <div class="task-parameters-templates-list_popover">
                      <TirPmInput
                        v-model="innerName"
                        :isWithHint="false"
                        label="Название шаблона"
                        placeholder="Шаблон"
                        ref="$nameInput"
                      />
                      <div class="task-parameters-templates-list_popover_buttons">
                        <TirPmButton
                          :variant="TirPmButtonVariantEnum.PrimaryState"
                          :size="TirPmButtonSizeEnum.Small"
                          :is-disabled="checkSaveDisable(template)"
                          @click="editTemplateName(template, index)"
                        >
                          Сохранить
                        </TirPmButton>
                        <TirPmButton
                          :variant="TirPmButtonVariantEnum.SecondaryState"
                          :size="TirPmButtonSizeEnum.Small"
                          @click="startEditTemplate(template, index)"
                        >
                          Редактировать состав
                        </TirPmButton>
                      </div>
                    </div>
                  </template>
                </TirPmPopover>
                <TirPmPopover :position="PopoverPositionEnum.Right" ref="$removePopover">
                  <template #default>
                    <div class="task-parameters-form_button">
                      <TrashIcon />
                    </div>
                  </template>
                  <template #content>
                    <div class="task-parameters-templates-list_popover">
                      <div class="task-parameters-templates-list_popover_buttons">
                        <TirPmButton
                          :variant="TirPmButtonVariantEnum.PrimaryState"
                          :size="TirPmButtonSizeEnum.Small"
                          @click="removeTemplate(template)"
                        >
                          Удалить
                        </TirPmButton>
                        <TirPmButton
                          :variant="TirPmButtonVariantEnum.SecondaryState"
                          :size="TirPmButtonSizeEnum.Small"
                          @click="closeRemovePopover(index)"
                        >
                          Отмена
                        </TirPmButton>
                      </div>
                    </div>
                  </template>
                </TirPmPopover>
              </template>
              <TirPmButton
                v-if="isImport || hoverIndex == index"
                :size="TirPmButtonSizeEnum.Small"
                :variant="TirPmButtonVariantEnum.SecondaryState"
                class="accept"
                @click="selectTemplate(template)"
              >
                <ArrowDownTrayIcon v-if="isImport" />
                <span v-else>Применить</span>
              </TirPmButton>
            </div>
            <div class="task-parameters-templates-list_item_parameters">
              <TaskParameter
                v-for="(parameter,idx) in template.ParsedData" :key="idx"
                preview
                :data="parameter"
                :type="type"
              />
            </div>
          </div>
        </TransitionGroup>
      </div>
    </TirPmScrollbar>
  </div>
</template>
<style lang="scss">
.task-parameters-templates-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 0.75rem;
  &_item {
    display: flex;
    flex-direction: column;
    padding: 1rem;
    gap: 0.75rem;
    border: 0.0625rem solid var(--text-qunternery, #bfc0c3);
    border-radius: 0.5rem;
    transition: 0.2s linear;
    &_title {
      display: flex;
      align-items: center;
      flex-wrap: nowrap;
      gap: 0.75rem;
      color: var(--text-secondary, #686c73);
      font-size: 1rem;
      font-weight: 500;
      .accept {
        margin-left: auto;
        padding: 0;
        min-width: 2rem;
        svg {
          width: 1rem;
          height: 1rem;
        }
      }
    }
    &_parameters {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem;
    }
    &.hover {
      border-color: var(--text-primary, #272d37);
    }
  }
  .tir-scroll {
    margin-right: -0.75rem;
    .line.vertical {
      overflow: visible;
    }
  }
  &_wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding-right: 0.75rem;
    max-height: 31.125rem;
  }
  .task-parameters-form_button {
    &.rotate > svg {
      transform: rotate(180deg);
    }
  }
  &_animation-enter-active,
  &_animation-leave-active {
    transition: 0.3s;
    opacity: 1;
  }
  &_animation-enter-from,
  &_animation-leave-to {
    transition: 0.3s;
    opacity: 0;
  }
  &_popover {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    &_buttons {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      svg {
        width: 1rem;
        height: 1rem;
      }
    }
  }
}
</style>
